import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import { Globe2, RefreshCw, Lock, Unlock, FileDown, AlertTriangle, Info, CheckCircle2, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import InfoHint from '../../SharedComponents/InfoHint';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageHeader from '../../../layouts/PageHeader';
import { NoData } from '../../SharedComponents/NoData';

/**
 * Periodisk sammanställning (G8).
 *
 * Deliberately its own page rather than a tab inside Momsrapport: it is a
 * different filing, on a different period, under different rules — most
 * sharply R-PS-3, where a period with nothing to report must NOT be filed,
 * the exact opposite of the momsdeklaration. Putting them in one place is how
 * that gets confused.
 *
 * No new visual language: the same PageHeader, modern-card and table patterns
 * the momsperiod list already uses.
 */

const STATE_BADGE = {
    open: { label: 'Öppen', background: 'var(--rgba-primary-8, #2f5e37)' },
    closed: { label: 'Låst', background: 'var(--primary)' },
};

function formatSek(value) {
    return new Intl.NumberFormat('sv-SE').format(Math.round(Number(value || 0)));
}

/** The file's column names in the words the rest of the page uses (NEW-3). */
const COLUMN_LABELS = {
    varuvarde: 'varuvärde',
    trepartshandelsvarde: 'trepartshandel',
    tjanstevarde: 'tjänstevärde',
};

const columnLabel = (column) => COLUMN_LABELS[column] || column;

/**
 * The server sends 'YYYY-MM-DD HH:MM:SS'. Seconds add nothing to a confirmation
 * the user just made, and a raw timestamp mid-sentence reads badly.
 */
function formatSubmittedAt(value) {
    if (!value) return '';
    const text = String(value).trim();
    const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    return match ? `${match[1]} kl. ${match[2]}` : text;
}

export default function PeriodiskSammanstallning() {
    const { companyId } = useParams();
    const [periods, setPeriods] = useState([]);
    const [selected, setSelected] = useState(null);
    const [report, setReport] = useState(null);
    const [backfill, setBackfill] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());

    const years = useMemo(() => {
        const current = new Date().getFullYear();
        return [current + 1, current, current - 1, current - 2];
    }, []);

    const loadPeriods = useCallback(async (targetYear) => {
        setLoading(true);
        try {
            const response = await axios.get(getApiUrl(`${companyId}/reporting/ps-periods`), {
                params: { year: targetYear }, withCredentials: true,
            });
            setPeriods(response.data.periods || []);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Perioderna kunde inte hämtas.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    const loadBackfill = useCallback(async () => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/ps-buyer-vat-backfill`), { withCredentials: true }
            );
            setBackfill(response.data);
        } catch (error) {
            setBackfill(null);
        }
    }, [companyId]);

    useEffect(() => { loadPeriods(year); }, [year, loadPeriods]);
    useEffect(() => { loadBackfill(); }, [loadBackfill]);

    const openPeriod = async (period) => {
        setSelected(period);
        setReport(null);
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/ps-period/${period.ps_period_id}`),
                { withCredentials: true }
            );
            setReport(response.data);
            // The response carries the period as the server has it. Using it
            // keeps `selected` honest — the confirm handler used to fabricate
            // `submitted_at: 'now'`, which rendered literally as "Bekräftad som
            // inlämnad now".
            if (response.data && response.data.period) setSelected(response.data.period);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Underlaget kunde inte hämtas.');
        }
    };

    const sync = async () => {
        setBusy(true);
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/ps-periods/sync`),
                { year }, { withCredentials: true }
            );
            toast.success(
                `Perioder uppdaterade (${response.data.cadence === 'monthly' ? 'månadsvis' : 'kvartalsvis'}): `
                + `${response.data.created.length} nya, ${response.data.updated.length} ändrade.`
            );
            await loadPeriods(year);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Perioderna kunde inte uppdateras.');
        } finally {
            setBusy(false);
        }
    };

    const freeze = async () => {
        setBusy(true);
        try {
            await axios.post(
                getApiUrl(`${companyId}/reporting/ps-period/${selected.ps_period_id}/freeze`),
                {}, { withCredentials: true }
            );
            toast.success('Perioden är låst.');
            await loadPeriods(year);
            await openPeriod(selected);
        } catch (error) {
            const data = error.response?.data;
            toast.error(data?.message || 'Perioden kunde inte låsas.');
        } finally {
            setBusy(false);
        }
    };

    const reopen = async () => {
        setBusy(true);
        try {
            await axios.post(
                getApiUrl(`${companyId}/reporting/ps-period/${selected.ps_period_id}/reopen`),
                {}, { withCredentials: true }
            );
            toast.success('Perioden är öppnad igen. Tidigare filer sparas som historik.');
            await loadPeriods(year);
            await openPeriod(selected);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Perioden kunde inte öppnas.');
        } finally {
            setBusy(false);
        }
    };

    // B4 — the same vocabulary as the momsdeklaration: LedgerLoop GENERATES,
    // the user CONFIRMS they submitted, and a later regeneration is a rättelse.
    // Skatteverket sends no receipt, so the confirmation is the user's own.
    const setSubmitted = async (submitted) => {
        setBusy(true);
        try {
            const url = getApiUrl(`${companyId}/reporting/ps-period/${selected.ps_period_id}/submitted`);
            if (submitted) await axios.post(url, {}, { withCredentials: true });
            else await axios.delete(url, { withCredentials: true });
            toast.success(submitted ? 'Inlämningen är bekräftad.' : 'Bekräftelsen är borttagen.');
            await loadPeriods(year);
            // No optimistic timestamp — openPeriod adopts the server's period,
            // so the banner shows when it was actually confirmed.
            await openPeriod(selected);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kunde inte uppdatera inlämningsstatusen.');
        } finally {
            setBusy(false);
        }
    };

    const generateFile = async () => {
        setBusy(true);
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/ps-period/${selected.ps_period_id}/generate-file`),
                {}, { withCredentials: true }
            );
            toast.success(`Filen ${response.data.file.file_name} är skapad.`);
            // Fetched through the API, not opened as a blob URL: the storage
            // account is private, so the bare address returned Azure's
            // PublicAccessNotPermitted instead of the file.
            const file = await axios.get(
                getApiUrl(`${companyId}/reporting/ps-period/${selected.ps_period_id}/download`),
                { withCredentials: true, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([file.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.data.file.file_name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // A refusal with neither `message` nor `problems` used to join to an
            // empty string, so toast.error('') rendered nothing and a 400 looked
            // like the button doing nothing at all. Always say something, and name
            // the status when the server gave no reason.
            const data = error.response?.data;
            const parts = [data?.message, ...(data?.problems || [])].filter(Boolean);
            const status = error.response?.status;
            toast.error(
                parts.length
                    ? parts.join(' ')
                    : `Filen kunde inte skapas${status ? ` (fel ${status})` : ''}. Försök igen, `
                      + 'eller kontrollera uppgifterna under Företagsuppgifter.'
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                icon={Globe2}
                title="Periodisk sammanställning"
                subtitle="EU-försäljning per köpare, att lämna till Skatteverket."
            />

            <div className="col-12">
                {backfill && backfill.customers.length > 0 && (
                    <Alert variant="warning" className="d-flex align-items-start">
                        <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                        <div>
                            {backfill.message}
                            <ul className="mb-0 mt-1">
                                {backfill.customers.slice(0, 8).map((customer) => (
                                    <li key={customer.customer_id}>
                                        {customer.customer_name} ({customer.country || 'okänt land'}) —
                                        {' '}{customer.invoice_count} faktura(or), {customer.first_invoice}–{customer.last_invoice}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Alert>
                )}

                <div className="modern-card">
                    <div className="modern-card-header">
                        <div>
                            <h2 className="section-title">Perioder {year}</h2>
                            <p className="section-subtitle">
                                Varor rapporteras per kalendermånad, enbart tjänster per kalenderkvartal.
                                Perioderna följer kalendern och har ingen koppling till
                                räkenskapsåret.
                            </p>
                        </div>
                        <div className="action-buttons d-flex gap-2 align-items-center">
                            {years.map((candidate) => (
                                <Button
                                    key={candidate}
                                    size="sm"
                                    variant={candidate === year ? 'primary' : 'outline-secondary'}
                                    onClick={() => { setYear(candidate); setSelected(null); setReport(null); }}
                                >
                                    {candidate}
                                </Button>
                            ))}
                            <Button variant="outline-primary" onClick={sync} disabled={busy}
                                className="d-flex align-items-center gap-2">
                                <RefreshCw size={16} />
                                Uppdatera perioder
                            </Button>
                        </div>
                    </div>

                    <div className="modern-card-body">
                        {loading && <Spinner animation="border" size="sm" />}
                        {!loading && periods.length === 0 ? (
                            <NoData message="Inga perioder för det valda året. Välj “Uppdatera perioder” så skapas de utifrån vad som sålts." />
                        ) : (
                            <div className="modern-table-container">
                                <Table responsive hover className="modern-table">
                                    <thead>
                                        <tr>
                                            <th>Period</th>
                                            <th>Kod</th>
                                            <th>
                                                Senast
                                                {/* The stored date is the earlier of the two
                                                    (paper), so the bare 20th read as the only
                                                    date available. R-PS-3. */}
                                                <InfoHint
                                                    text={
                                                        'Pappersblanketten (SKV 5740) ska vara inne '
                                                        + 'den 20:e i månaden efter perioden, '
                                                        + 'e-tjänsten den 25:e. Datumet här visar det '
                                                        + 'tidigare av de två — lämnar du elektroniskt '
                                                        + 'har du några dagar till. Infaller dagen på '
                                                        + 'en helg eller helgdag flyttas den till nästa '
                                                        + 'vardag.'
                                                    }
                                                    size={13}
                                                />
                                            </th>
                                            <th>Status</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {periods.map((period) => (
                                            <tr key={period.ps_period_id}
                                                style={selected?.ps_period_id === period.ps_period_id
                                                    ? { background: 'rgba(47,94,55,0.06)' } : undefined}>
                                                <td>{period.start_date} – {period.end_date}</td>
                                                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{period.period_code}</td>
                                                <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {period.due_date}
                                                    {period.due_date_statutory
                                                        && period.due_date_statutory !== period.due_date && (
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--secondary, #717579)' }}>
                                                            flyttad från {period.due_date_statutory} (helgdagsregeln)
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge bg="" style={{ background: STATE_BADGE[period.state].background }}>
                                                        {STATE_BADGE[period.state].label}
                                                    </Badge>
                                                    {period.state === 'closed' && Number(period.filing_required) === 0 && (
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--secondary, #717579)' }}>
                                                            ingen sammanställning skulle lämnas
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <Button size="sm" variant="outline-secondary"
                                                        onClick={() => openPeriod(period)}>
                                                        Granska
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>

                {selected && (
                    <div className="modern-card">
                        <div className="modern-card-header">
                            <div>
                                <h2 className="section-title">
                                    {selected.start_date} – {selected.end_date}
                                </h2>
                                {/* The cadence reason is resolved from the tenant's
                                    lookback window, not from this period's rows, so
                                    "Ingen rapporteringspliktig försäljning ännu" could
                                    sit directly above a 10 000 kr buyer row and a green
                                    "underlaget stämmer" banner. When this period does
                                    have something to file, the period's own fact wins
                                    and the cadence is stated without the claim. */}
                                <p className="section-subtitle">
                                    {Number(selected.filing_required) === 1
                                        ? `Periodisk sammanställning ska lämnas för perioden. ${
                                            selected.cadence === 'monthly'
                                                ? 'Rapporteras per kalendermånad.'
                                                : 'Rapporteras per kalenderkvartal.'}`
                                        : selected.cadence_reason}
                                </p>
                            </div>
                            <div className="action-buttons d-flex gap-2">
                                {selected.state === 'open' ? (
                                    <Button variant="primary" onClick={freeze} disabled={busy}
                                        className="d-flex align-items-center gap-2">
                                        <Lock size={16} /> Lås perioden
                                    </Button>
                                ) : (
                                    <>
                                        <Button variant="outline-secondary" onClick={reopen} disabled={busy}
                                            className="d-flex align-items-center gap-2">
                                            <Unlock size={16} /> Öppna igen
                                        </Button>
                                        {Number(selected.filing_required) === 1 && (
                                            <Button variant="primary" onClick={generateFile} disabled={busy}
                                                className="d-flex align-items-center gap-2">
                                                <FileDown size={16} /> Skapa fil (SKV574008)
                                            </Button>
                                        )}
                                        {Number(selected.filing_required) === 1 && !selected.submitted_at && (
                                            <Button variant="success" onClick={() => setSubmitted(true)} disabled={busy}
                                                className="d-flex align-items-center gap-2">
                                                <FileCheck size={16} /> Bekräfta inlämning
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="modern-card-body">
                            {/* NEW-3 — a reopened period has had its confirmation
                                cleared: it is a correction in progress, not
                                something that has been filed. */}
                            {selected.submitted_at && selected.state === 'closed' && (
                                <Alert variant="success" className="d-flex align-items-start">
                                    <FileCheck size={18} className="me-2 mt-1 flex-shrink-0" />
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold">
                                            Bekräftad som inlämnad {formatSubmittedAt(selected.submitted_at)}
                                        </div>
                                        Bekräftelsen är din egen — LedgerLoop får ingen kvittens från
                                        Skatteverket och kan inte se om filen tagits emot. Öppnar du perioden
                                        igen blir nästa fil en rättelse.
                                        <div>
                                            <Button size="sm" variant="link" className="p-0"
                                                onClick={() => setSubmitted(false)} disabled={busy}>
                                                Ta bort bekräftelsen
                                            </Button>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                            {!report ? <Spinner animation="border" size="sm" /> : (
                                <>
                                    {!report.filing_required && (
                                        <Alert variant="light" className="d-flex align-items-start">
                                            <Info size={18} className="me-2 mt-1 flex-shrink-0" />
                                            <div>
                                                Perioden innehåller ingen försäljning som ska rapporteras.
                                                Till skillnad från momsdeklarationen ska ingen periodisk
                                                sammanställning lämnas då — det finns ingen nollrapport.
                                            </div>
                                        </Alert>
                                    )}

                                    {/* B2 — a filing IS due and LedgerLoop cannot build every
                                        row of it. Saying "ingen sammanställning ska lämnas"
                                        here would be a false statement of law, told to exactly
                                        the tenant who most needs the opposite. */}
                                    {report.filing_required && report.rows_incomplete && (
                                        <Alert variant="warning" className="d-flex align-items-start">
                                            <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                            <div>
                                                <div className="fw-semibold">
                                                    Sammanställningen ska lämnas — men LedgerLoop kan inte
                                                    ta fram alla rader
                                                </div>
                                                Momsdeklarationen visar rapporteringspliktig försäljning i
                                                {' '}{report.incomplete_columns.map(columnLabel).join(', ')} som
                                                inte går att knyta till en köpare här: det finns ingen faktura
                                                med köparens momsnummer bakom den. Komplettera raderna manuellt
                                                i Skatteverkets e-tjänst innan du lämnar in.
                                            </div>
                                        </Alert>
                                    )}

                                    {/* The opposite direction to the block above. The
                                        sammanställning follows the invoice; under
                                        kontantmetoden the momsdeklaration follows the
                                        payment, so an unpaid invoice shows here first.
                                        Nothing to correct — said so it does not read as
                                        an error. */}
                                    {report.rows_exceed_declaration && (
                                        <Alert variant="light" className="d-flex align-items-start">
                                            <Info size={18} className="me-2 mt-1 flex-shrink-0" />
                                            <div>
                                                <div className="fw-semibold">
                                                    Fakturaunderlaget är större än momsdeklarationen
                                                </div>
                                                Fakturorna i
                                                {' '}{report.surplus_columns.map(columnLabel).join(', ')}
                                                {' '}summerar till mer än momsdeklarationen visar. Den
                                                vanligaste förklaringen är en faktura som ännu inte
                                                betalats: sammanställningen följer fakturan, medan
                                                momsen med kontantmetoden redovisas först när
                                                betalningen kommit in. Stämmer det behöver du inte
                                                göra något.
                                            </div>
                                        </Alert>
                                    )}

                                    {report.missing_vat_number.length > 0 && (
                                        <Alert variant="danger" className="d-flex align-items-start">
                                            <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                            <div>
                                                {report.missing_vat_number.length} köpare saknar giltigt
                                                momsregistreringsnummer och perioden kan därför inte låsas.
                                                Försäljningen är momsfri just för att köparen är momsregistrerad
                                                i sitt land, och numret ska stå både på fakturan och i
                                                sammanställningen.
                                                <ul className="mb-0 mt-1">
                                                    {report.missing_vat_number.map((buyer) => (
                                                        <li key={buyer.customer_id}>
                                                            {buyer.customer_name} ({buyer.country || 'okänt land'})
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </Alert>
                                    )}

                                    {report.differences.length > 0 && (
                                        <Alert variant="warning" className="d-flex align-items-start">
                                            <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                            <div>
                                                Momsdeklarationens fält och fakturaunderlaget skiljer sig åt.
                                                Sammanställningen byggs på fakturorna, eftersom det bara är där
                                                köparens momsnummer finns — en skillnad betyder att något bokförts
                                                utan faktura, till exempel en mellanmans trepartshandel eller en
                                                överföring av egna varor.
                                                <ul className="mb-0 mt-1">
                                                    {/* NEW-4 — the three figures are the SAME rounded
                                                        numbers the difference was computed from, so
                                                        deklaration − fakturor always equals skillnad on
                                                        screen. Showing two independently rounded
                                                        operands beside their difference made the
                                                        arithmetic look wrong by a krona. */}
                                                    {report.differences.map((difference) => {
                                                        const invoices = difference.from_declaration - difference.difference;
                                                        return (
                                                            <li key={difference.column}>
                                                                {columnLabel(difference.column)}: fakturor {formatSek(invoices)} kr,
                                                                {' '}deklaration {formatSek(difference.from_declaration)} kr
                                                                {' '}(skillnad {formatSek(difference.difference)} kr)
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </Alert>
                                    )}

                                    {report.differences.length === 0 && report.filing_required && (
                                        <Alert variant="light" className="d-flex align-items-start">
                                            <CheckCircle2 size={18} className="me-2 mt-1 flex-shrink-0" />
                                            <div>
                                                Underlaget stämmer med fält 35, 38 och 39 i momsdeklarationen
                                                för samma period.
                                            </div>
                                        </Alert>
                                    )}

                                    <div className="modern-table-container">
                                        <Table responsive className="modern-table">
                                            <thead>
                                                <tr>
                                                    <th>Köpare</th>
                                                    <th>Momsregistreringsnummer</th>
                                                    <th className="text-end">Varuvärde</th>
                                                    <th className="text-end">Trepartshandel</th>
                                                    <th className="text-end">Tjänstevärde</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {report.buyers.map((buyer) => (
                                                    <tr key={buyer.customer_id}>
                                                        <td>{buyer.customer_name}</td>
                                                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                            {buyer.buyer_vat_number || (
                                                                <span style={{ color: 'var(--danger, #b02a37)' }}>saknas</span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">{formatSek(buyer.varuvarde)}</td>
                                                        <td className="text-end">{formatSek(buyer.trepartshandelsvarde)}</td>
                                                        <td className="text-end">{formatSek(buyer.tjanstevarde)}</td>
                                                    </tr>
                                                ))}
                                                <tr style={{ fontWeight: 600 }}>
                                                    <td colSpan={2}>Summa</td>
                                                    <td className="text-end">{formatSek(report.totals.varuvarde)}</td>
                                                    <td className="text-end">{formatSek(report.totals.trepartshandelsvarde)}</td>
                                                    <td className="text-end">{formatSek(report.totals.tjanstevarde)}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
